import {
  Card,
  Divider,
  ScrollArea,
  Stack,
  TypographyStylesProvider,
} from '@mantine/core';
import { isValidElement, type ReactElement, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import PageHeader from '../../components/shared/PageHeader';
import guideMarkdown from '../../../docs/ricerca-normalizzazione-spettrale-guida-utente.md?raw';

interface StaticDiagramProps {
  kind: string;
}

const PipelineMainDiagram = () => (
  <svg
    viewBox='0 0 940 360'
    width='100%'
    role='img'
    aria-label='Pipeline principale'
  >
    <defs>
      <marker
        id='arrow-main'
        markerWidth='10'
        markerHeight='10'
        refX='9'
        refY='3'
        orient='auto'
      >
        <path d='M0,0 L0,6 L9,3 z' fill='currentColor' />
      </marker>
    </defs>
    <g fontFamily='sans-serif' fontSize='14' fill='currentColor'>
      <rect
        x='20'
        y='24'
        width='190'
        height='44'
        rx='8'
        fill='none'
        stroke='currentColor'
      />
      <text x='34' y='51'>
        Input sessioni Polar 360
      </text>

      <rect
        x='250'
        y='24'
        width='200'
        height='44'
        rx='8'
        fill='none'
        stroke='currentColor'
      />
      <text x='264' y='51'>
        Qualita + allineamento
      </text>

      <rect
        x='490'
        y='24'
        width='200'
        height='44'
        rx='8'
        fill='none'
        stroke='currentColor'
      />
      <text x='514' y='51'>
        Pulizia duplicati/gap
      </text>

      <rect
        x='730'
        y='24'
        width='190'
        height='44'
        rx='8'
        fill='none'
        stroke='currentColor'
      />
      <text x='754' y='51'>
        Segmenti pre/post
      </text>

      <rect
        x='340'
        y='112'
        width='260'
        height='44'
        rx='8'
        fill='none'
        stroke='currentColor'
      />
      <text x='356' y='139'>
        HRV preprocessing (NN, artefatti, 4 Hz)
      </text>

      <rect
        x='220'
        y='200'
        width='220'
        height='44'
        rx='8'
        fill='none'
        stroke='currentColor'
      />
      <text x='248' y='227'>
        RMSSD da delta NN
      </text>

      <rect
        x='500'
        y='200'
        width='230'
        height='44'
        rx='8'
        fill='none'
        stroke='currentColor'
      />
      <text x='516' y='227'>
        Welch PSD + bande LF/HF
      </text>

      <rect
        x='350'
        y='288'
        width='250'
        height='44'
        rx='8'
        fill='none'
        stroke='currentColor'
      />
      <text x='386' y='315'>
        Output UI + CSV + snapshot
      </text>

      <line
        x1='210'
        y1='46'
        x2='250'
        y2='46'
        stroke='currentColor'
        markerEnd='url(#arrow-main)'
      />
      <line
        x1='450'
        y1='46'
        x2='490'
        y2='46'
        stroke='currentColor'
        markerEnd='url(#arrow-main)'
      />
      <line
        x1='690'
        y1='46'
        x2='730'
        y2='46'
        stroke='currentColor'
        markerEnd='url(#arrow-main)'
      />
      <line
        x1='825'
        y1='68'
        x2='540'
        y2='112'
        stroke='currentColor'
        markerEnd='url(#arrow-main)'
      />
      <line
        x1='470'
        y1='156'
        x2='330'
        y2='200'
        stroke='currentColor'
        markerEnd='url(#arrow-main)'
      />
      <line
        x1='470'
        y1='156'
        x2='615'
        y2='200'
        stroke='currentColor'
        markerEnd='url(#arrow-main)'
      />
      <line
        x1='330'
        y1='244'
        x2='455'
        y2='288'
        stroke='currentColor'
        markerEnd='url(#arrow-main)'
      />
      <line
        x1='615'
        y1='244'
        x2='495'
        y2='288'
        stroke='currentColor'
        markerEnd='url(#arrow-main)'
      />
    </g>
  </svg>
);

const PipelineBranchesDiagram = () => (
  <svg
    viewBox='0 0 940 300'
    width='100%'
    role='img'
    aria-label='Confronto rami A e B'
  >
    <defs>
      <marker
        id='arrow-branch'
        markerWidth='10'
        markerHeight='10'
        refX='9'
        refY='3'
        orient='auto'
      >
        <path d='M0,0 L0,6 L9,3 z' fill='currentColor' />
      </marker>
    </defs>
    <g fontFamily='sans-serif' fontSize='14' fill='currentColor'>
      <rect
        x='24'
        y='22'
        width='210'
        height='44'
        rx='8'
        fill='none'
        stroke='currentColor'
      />
      <text x='40' y='49'>
        Input wearable ~1 Hz
      </text>

      <rect
        x='274'
        y='22'
        width='250'
        height='44'
        rx='8'
        fill='none'
        stroke='currentColor'
      />
      <text x='296' y='49'>
        Preprocessing strutturale comune
      </text>

      <rect
        x='560'
        y='96'
        width='320'
        height='44'
        rx='8'
        fill='none'
        stroke='currentColor'
      />
      <text x='576' y='123'>
        Ramo B (primario): NN + 4 Hz + Welch
      </text>

      <rect
        x='560'
        y='178'
        width='320'
        height='44'
        rx='8'
        fill='none'
        stroke='currentColor'
      />
      <text x='576' y='205'>
        Ramo A (supporto): z-score + filtro + Welch
      </text>

      <rect
        x='560'
        y='258'
        width='320'
        height='30'
        rx='8'
        fill='none'
        stroke='currentColor'
      />
      <text x='586' y='278'>
        Interpretazione finale pre/post
      </text>

      <line
        x1='234'
        y1='44'
        x2='274'
        y2='44'
        stroke='currentColor'
        markerEnd='url(#arrow-branch)'
      />
      <line
        x1='524'
        y1='44'
        x2='560'
        y2='116'
        stroke='currentColor'
        markerEnd='url(#arrow-branch)'
      />
      <line
        x1='524'
        y1='44'
        x2='560'
        y2='198'
        stroke='currentColor'
        strokeDasharray='6 4'
        markerEnd='url(#arrow-branch)'
      />
      <line
        x1='720'
        y1='140'
        x2='720'
        y2='258'
        stroke='currentColor'
        markerEnd='url(#arrow-branch)'
      />
      <line
        x1='720'
        y1='222'
        x2='760'
        y2='258'
        stroke='currentColor'
        markerEnd='url(#arrow-branch)'
      />
    </g>
  </svg>
);

const StaticDiagram = ({ kind }: StaticDiagramProps) => {
  if (kind === 'pipeline-main') return <PipelineMainDiagram />;
  if (kind === 'pipeline-branches') return <PipelineBranchesDiagram />;

  return (
    <pre>
      <code>{kind}</code>
    </pre>
  );
};

export function SpectralNormalizationDocsPage() {
  const markdown = useMemo(() => guideMarkdown, []);

  return (
    <Stack gap='md'>
      <PageHeader
        title='Documentazione'
        subtitle='Guida utente della Normalizzazione spettrale.'
        breadcrumbs={[
          { label: 'Ricerca', href: '/research' },
          {
            label: 'Normalizzazione spettrale',
            href: '/research/spectral-normalization',
          },
          { label: 'Documentazione' },
        ]}
        backUrl='/research/spectral-normalization'
      />

      <Divider my='md' />

      <Card withBorder padding='xl' radius='lg'>
        <TypographyStylesProvider
          style={{
            lineHeight: 1.7,
            fontSize: '15px',
          }}
        >
          <div style={{ maxWidth: 920, margin: '0 auto' }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                pre(props) {
                  const { children } = props;
                  const firstChild = Array.isArray(children)
                    ? children[0]
                    : children;

                  if (isValidElement(firstChild)) {
                    const codeNode = firstChild as ReactElement<{
                      className?: string;
                      children?: unknown;
                    }>;
                    if (
                      typeof codeNode.props.className === 'string' &&
                      codeNode.props.className.includes('language-mermaid')
                    ) {
                      return (
                        <pre>
                          <code>{String(codeNode.props.children ?? '')}</code>
                        </pre>
                      );
                    }

                    if (
                      typeof codeNode.props.className === 'string' &&
                      codeNode.props.className.includes('language-diagram')
                    ) {
                      const kind = String(codeNode.props.children ?? '')
                        .trim()
                        .replace(/\n/g, '');
                      return <StaticDiagram kind={kind} />;
                    }
                  }

                  return <pre>{children}</pre>;
                },
                code(props) {
                  const { className, children } = props;
                  return <code className={className}>{children}</code>;
                },
                table(props) {
                  return (
                    <ScrollArea type='auto' offsetScrollbars>
                      <table {...props} />
                    </ScrollArea>
                  );
                },
              }}
            >
              {markdown}
            </ReactMarkdown>
          </div>
        </TypographyStylesProvider>
      </Card>
    </Stack>
  );
}

export default SpectralNormalizationDocsPage;
